import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Navbar from "./Navbar";
import DashboardHeader from "./DashboardHeader";
import Footer from "./Footer";
import api from "../api";
import { useTheme } from "../App"; 

/* ================= HELPERS ================= */

// Extract strings gracefully
const extractText = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === 'object') return val.text !== undefined ? String(val.text) : "";
  return String(val);
};


const getCurrentMonthName = () => {
  const date = new Date();
  return date.toLocaleString("default", { month: "long", year: "numeric" });
};

const generateMonthOptions = () => {
  const months = [];
  const date = new Date();
  date.setMonth(date.getMonth() - 3); 
  for (let i = 0; i < 12; i++) {
    months.push(date.toLocaleString('default', { month: 'long', year: 'numeric' }));
    date.setMonth(date.getMonth() + 1);
  }
  return months;
};


const ACHIEVEMENT_EDIT_WINDOW_MINUTES = 15;
const GOAL_EDIT_WINDOW_MINUTES = 15;

const canEditAchievement = (goal) => {
  const achievementText = extractText(goal.achievements).trim();
  if (!achievementText) return true;
  if (!goal.achievementSubmittedAt) return false;
  const submittedAt = new Date(goal.achievementSubmittedAt);
  if (isNaN(submittedAt.getTime())) return false;
  const now = new Date();
  return ((now - submittedAt) / (1000 * 60)) <= ACHIEVEMENT_EDIT_WINDOW_MINUTES;
};

const canEditGoal = (goal) => {
  if (!goal.createdAt) return false;
  const createdAt = new Date(goal.createdAt);
  if (isNaN(createdAt.getTime())) return false;
  const now = new Date();
  return ((now - createdAt) / (1000 * 60)) <= GOAL_EDIT_WINDOW_MINUTES;
};

const withEditWindow = (goal) => ({
  ...goal,
  canEditAchievement: canEditAchievement(goal),
  canEditGoal: canEditGoal(goal),
});

const getGoalUnit = (goal) => {
  let unitVal = goal.unit || goal.subDepartment || "";
  if (typeof unitVal !== 'string') unitVal = String(unitVal);
  return unitVal;
};

/* ================= PAGE LAYOUT & THEME VARIABLES ================= */
const Container = styled.div`
  --sidebar-expanded: 260px;
  --sidebar-collapsed: 90px;
  
  --page-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #edf4ff 100%)'};
  --card-bg: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : 'rgba(255, 255, 255, 0.96)'};
  --text-main: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
  --text-soft: ${({ $isDarkMode }) => $isDarkMode ? '#94a3b8' : '#64748b'};
  --border: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(148, 163, 184, 0.18)'};
  --input-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#ffffff'};
  --input-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#e5e7eb'};
  --table-header-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#f8fafc'};
  --table-row-alt: ${({ $isDarkMode }) => $isDarkMode ? '#162032' : '#f0f9ff'};
  --table-row-hover: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : '#ffffff'};
  --table-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#e2e8f0'};
  
  --primary: #2563eb;
  --primary-dark: #1d4ed8;
  --success: #16a34a;
  --warning: #ea580c;
  --shadow-lg: 0 22px 55px rgba(0, 0, 0, 0.15);
  --shadow-md: 0 12px 30px rgba(0, 0, 0, 0.1);

  min-height: 100vh;
  background: var(--page-bg);
  color: var(--text-main);
  margin-left: ${({ collapsed }) => collapsed ? "var(--sidebar-collapsed)" : "var(--sidebar-expanded)"};
  transition: margin-left 0.3s ease, background 0.3s ease, color 0.3s ease;
  @media (max-width: 1023px) { margin-left: 0; }
`;

// const Main = styled.main`
//   width: min(1380px, calc(100% - 32px)); margin: 0 auto; padding:34px 0 80px;
//   @media (max-width: 1023px) { width: min(100%, calc(100% - 24px)); padding-top: 86px; }
//   @media (max-width: 576px) { width: min(100%, calc(100% - 16px)); }
// `;
const Main = styled.main`
  width: min(1380px, calc(100% - 32px)); 
  margin: 0 auto; 
  padding: 24px 0 80px;

  @media (max-width: 1023px) { 
    width: min(100%, calc(100% - 24px)); 
    padding-top: 24px; /* 🔥 Reduced from 86px */
  }

  @media (max-width: 576px) { 
    width: min(100%, calc(100% - 16px)); 
    padding-top: 0px; /* 🔥 Even tighter for mobile */
  }
`;

const VideoHeading = styled.h2`
  margin: 0 0 12px 4px; /* 🔥 Fixed missing 'px' and cleaned up margins */
  color: var(--text-main);
  font-size: 18px;
  font-weight: bold;
`;

const SalvationSection = styled.section`
  margin: 70px auto 0;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    margin: 40px auto 0; /* 🔥 Tighter mobile spacing */
  }
`;

const SalvationCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  color: #1f2937;
  border-radius: 24px;
  max-width: 720px;
  width: 100%;
  padding: 30px 45px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px 20px; /* 🔥 Prevents text from being squished on mobile */
  }
`;

const GrowFaithSection = styled.section`
  margin: 70px auto;
  max-width: 1200px;
  padding: 0 20px;
  background: #1f2937;
  border-radius: 24px;
  padding-bottom: 50px;

  @media (max-width: 768px) {
    margin: 40px auto; /* 🔥 Tighter mobile spacing */
    padding-bottom: 30px;
  }
`;

const ResourcesSection = styled.section`
  margin: 40px auto;
  max-width: 1200px;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0 16px; /* 🔥 Gives the cards more room to breathe on mobile */
    margin: 30px auto;
  }
`;

const PrayerTestimonySection = styled.section`
  margin: 60px auto;
  max-width: 1100px;
  padding: 0 20px;

  @media (max-width: 768px) {
    margin: 35px auto; /* 🔥 Tighter mobile spacing */
  }
`;
/* ================= MINISTRY SECTION ================= */

const MinistrySection = styled.section`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const VideoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VideoCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 420px;
  object-fit: cover;
  background: #000;

  @media (max-width: 768px) {
    height: 240px;
  }
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  color: white;
  background: linear-gradient(
    135deg,
    #2563eb,
    #3b82f6
  );

  transition: 0.3s;

  &:hover {
    transform: translateY(-2px);
  }
`;


const PrayerCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow-md);

  display: flex;
  flex-direction: column;
`;

const PrayerDate = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #2563eb;
  margin-bottom: 18px;
`;

const PrayerHeading = styled.h2`
  margin: 0 0 15px;
  color: var(--text-main);
  font-size: 24px;
`;
// const VideoHeading = styled.h2`
//   margin: 10 0 5px;
//   color: var(--text-main);
//   font-size: 18px;
//   font-weight: bold;
//   margin-left:5px
// `;


const PrayerText = styled.p`
  color: var(--text-soft);
  line-height: 1.8;
  flex: 1;
`;

const PrayButton = styled.button`
  margin-top: 20px;
  border: none;
  border-radius: 14px;
  padding: 15px;

  background: linear-gradient(
    135deg,
    #16a34a,
    #22c55e
  );

  color: white;
  font-weight: 700;
  cursor: pointer;

  transition: 0.3s;

  &:hover {
    transform: translateY(-2px);
  }
`;



const CounsellorSection = styled.section`
  margin-top: 30px;
  margin-left: auto;
  margin-right: auto;
  max-width: 1100px;
  border-radius: 24px;
  overflow: hidden;
  background: 
    linear-gradient(
      rgba(30, 58, 138, 0.85), 
      rgba(37, 99, 235, 0.82)
    ),
    url('https://fastly.picsum.photos/id/60/1920/600.jpg?hmac=rksxdBGMkS23axor53cwRXj8qlW03bKGK-wjkDDkb4E') center/cover no-repeat;
  color: white;
  padding: 55px 40px;
  box-shadow: 0 20px 50px rgba(37, 99, 235, 0.35);
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 40px 25px;
    border-radius: 20px;
  }
`;

const CounsellorTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.5px;
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const CounsellorSubtitle = styled.h3`
  margin: 0 0 25px 0;
  font-size: 23px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  @media (max-width: 768px) {
    font-size: 19px;
  }
`;

const CounsellorText = styled.p`
  font-size: 16.5px;
  line-height: 1.85;
  max-width: 820px;
  margin: 0 auto 35px auto;
  color: rgba(255, 255, 255, 0.93);
  @media (max-width: 768px) {
    font-size: 15.5px;
  }
`;

const CounsellorButton = styled.div`
  display: flex;
  gap: 18px;
  justify-content: center;
  flex-wrap: wrap;
`;

const CounsellorButtons = styled.button`
  border: 2px solid white;
  border-radius: 50px;           /* Very nice rounded look */
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  background: transparent;
  color: white;
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  transition: all 0.4s ease;
  letter-spacing: 0.3px;
  min-width: 220px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
  }

  &:active {
    transform: scale(0.97);
  }

  @media (max-width: 768px) {
    min-width: 200px;
    padding: 14px 28px;
  }
`;

/* ================= EVERYTHING YOU NEED SECTION ================= */


const ResourcesHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const ResourcesTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  color: var(--text-main);
  margin: 0 0 12px 0;
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ResourcesSubtitle = styled.p`
  font-size: 18px;
  color: var(--text-soft);
  max-width: 400px;
  margin: 0 auto;
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
`;

const ResourceCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 20px 32px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-md);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-lg);
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: rgba(37, 99, 235, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
`;

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-main);
  margin: 0 0 16px 0;
`;

const CardDescription = styled.p`
  color: var(--text-soft);
  line-height: 1.7;
  margin-bottom: 28px;
  min-height: 80px;
`;

const CardButton = styled.button`
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-size: 15.5px;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(37, 99, 235, 0.3);
  }
`;

/* ================= PRAYER REQUESTS & TESTIMONIES SECTION ================= */



const PrayerHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const PrayerTitle = styled.h2`
  font-size: 26px;
  font-weight: bold;
  color: var(--text-main);
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const PrayerTabs = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-soft);
  margin-bottom: 40px;

  span {
    color: var(--primary);
    font-weight: 700;
  }
`;

const PrayerButtonsContainer = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrayerButton = styled.button`
  border: none;
  border-radius: 50px;
  padding: 15px 35px;
  font-size: 17px;
  font-weight: bold;
  cursor: pointer;
  min-width: 260px;
  transition: all 0.4s ease;
  box-shadow: var(--shadow-md);

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 320px;
  }
`;

const NeedPrayerButton = styled(PrayerButton)`
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;

  &:hover {
    box-shadow: 0 15px 30px rgba(37, 99, 235, 0.4);
  }
`;

const TestimonyButton = styled(PrayerButton)`
  background: linear-gradient(135deg, #16a34a, #22c55e);
  color: white;

  &:hover {
    box-shadow: 0 15px 30px rgba(22, 163, 74, 0.4);
  }
`;

/* ================= COMPONENT ================= */
const Dashboard = ({ user }) => {
  const [collapsed, setCollapsed] = useState(false);
  const availableMonths = useMemo(() => generateMonthOptions(), []);

  const { isDarkMode } = useTheme();

/* ================= SALVATION CONGRATULATIONS SECTION ================= */

/* ================= SALVATION CONGRATULATIONS SECTION ================= */

const SalvationSection = styled.section`
  margin: 70px auto;
  max-width: 1200px;
  margin-bottom:0
  ${'' /* padding: 0 20px; */}
`;

const SalvationBackground = styled.div`
  border-radius: 24px;
  overflow: hidden;
  background: 
    linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)),
    url('https://forestgreen-caterpillar-516368.hostingersite.com/l/public/assets/img/salvation-banner.jpeg') center/cover no-repeat;
  min-height: 50px;           /* Better height for full image visibility */
  padding: 40px 10px;
  position: relative;
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  resize-mode: contain
  
`;

const SalvationCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  color: #1f2937;
  border-radius: 24px;
  max-width: 720px;
  width: 100%;
  padding: 10px 45px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const SalvationSmallText = styled.p`
  font-size: 17px;
  margin: 0 0 12px 0;
  color: #374151;
`;

const SalvationCongrats = styled.h2`
  font-size: 42px;
  font-weight: 900;
  color: #d97706;
  margin: 0 0 20px 0;
  letter-spacing: -1px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SalvationBookText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 30px 0;
  color: #1f2937;
`;

const DownloadButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 16px 48px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(245, 158, 11, 0.4);
  transition: all 0.4s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px rgba(245, 158, 11, 0.5);
  }
`;
/* ================= GROW YOUR FAITH SECTION ================= */

const GrowFaithSection = styled.section`
  margin: 70px auto;
  max-width: 1200px;
  padding: 0 20px;
  background: #1f2937;
  border-radius: 24px;
  padding-bottom: 50px;
`;

const GrowHeader = styled.div`
  text-align: center;
  padding: 60px 20px 40px;
`;

const GrowTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const GrowSubtitle = styled.p`
  font-size: 18px;
  color: #fff;
  max-width: 700px;
  margin: 0 auto;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 28px;
  padding: 0 10px;
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  height: 200px;
  background: #e5e7eb;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: rgba(37, 99, 235, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
  transition: all 0.3s ease;

  ${VideoCard}:hover & {
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const VideoInfo = styled.div`
  padding: 20px;
  color: #1f2937;
`;

const VideoTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const VideoDate = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0;
`;

  return (
    <Container collapsed={collapsed} $isDarkMode={isDarkMode}>
      <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
      <DashboardHeader user={user} collapsed={collapsed} title="Dashboard" />

      <Main>
<MinistrySection>

  <VideoColumn>
    <VideoHeading>
      Global Communion Service February Edition
    </VideoHeading>
    <VideoCard>

      <VideoPlayer controls>
        <source
          src="https://cdn1.kingschat.online/uploads/media/57fbe8f538da4d5569ad1cff/d05QaWd1VGhpdkI1cHRTamN0UDJqUT09/RECEIVE_SALVATION.mp4"
          type="video/mp4"
        />
      </VideoPlayer>

    </VideoCard>

    <ActionButtons>

      <ActionButton
        onClick={() => alert("Live Church")}
      >
        Live Church
      </ActionButton>

      <ActionButton
        onClick={() => alert("Give")}
      >
        Give
      </ActionButton>

      <ActionButton
        onClick={() => alert("Prayer Request")}
      >
        Prayer Request
      </ActionButton>

    </ActionButtons>

  </VideoColumn>

  <PrayerCard>
    
    <PrayerDate>
      Pray-A-Thon 2026- {new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric"
      })}
    </PrayerDate>

    <PrayerHeading>
      Prayer Point
    </PrayerHeading>

    <PrayerText>
Matthew 19:14 -

But Jesus said, 'Let the little children come to Me, and do not forbid them; for of such is the kingdom of heaven'.

At our times of prayer today, we'll pray fervently in tongues of the Spirit, interceding for children around the world, that they be saved from corrupting influences and doctrines, protected from harm, and that the unsaved amongst them will come to the knowledge of the truth. God bless you.
    </PrayerText>

    <PrayButton>
      Pray Now
    </PrayButton>

  </PrayerCard>

</MinistrySection>
<CounsellorSection>

  <CounsellorTitle>
    Welcome Church Online
  </CounsellorTitle>

  <CounsellorSubtitle>
    Counsellors Corner
  </CounsellorSubtitle>

  <CounsellorText>
    We are here to guide you in the faith of our Lord Jesus Christ
    and pray with you.

    <br /><br />

    📞 Call us on <strong>+234 703 895 5477</strong>

    <br />

    💬 Send us SMS on <strong>+234 803 892 3133</strong>
  </CounsellorText>

  <CounsellorButton>

    <CounsellorButtons
      onClick={() => alert("Receive Christ")}
    >
      I Want To Receive Christ
    </CounsellorButtons>

    <CounsellorButtons
      onClick={() => alert("Join A Cell")}
    >
      Join A Cell
    </CounsellorButtons>

  </CounsellorButton>

</CounsellorSection>
<ResourcesSection>
  <ResourcesHeader>
    <ResourcesTitle>Everything You Need</ResourcesTitle>
    <ResourcesSubtitle>
      Tools and resources to grow your faith journey.
    </ResourcesSubtitle>
  </ResourcesHeader>

  <ResourcesGrid>
    {/* Live Streaming Card */}
    <ResourceCard>
      <IconWrapper>📺</IconWrapper>
      <CardTitle>Live Streaming</CardTitle>
      <CardDescription>
        HD streaming with real-time chat and prayer requests.
      </CardDescription>
      <CardButton onClick={() => alert("Watch Live")}>
        Watch Now
      </CardButton>
    </ResourceCard>

    {/* Foundation School Card */}
    <ResourceCard>
      <IconWrapper>📖</IconWrapper>
      <CardTitle>Foundation School</CardTitle>
      <CardDescription>
        Structured courses with progress tracking.
      </CardDescription>
      <CardButton onClick={() => alert("Start Foundation School")}>
        Start Learning
      </CardButton>
    </ResourceCard>

    {/* Message Archive Card */}
    <ResourceCard>
      <IconWrapper>📚</IconWrapper>
      <CardTitle>Message Archive</CardTitle>
      <CardDescription>
        Access thousands of messages by topic.
      </CardDescription>
      <CardButton onClick={() => alert("Browse Archive")}>
        Browse Library
      </CardButton>
    </ResourceCard>
  </ResourcesGrid>
</ResourcesSection>
<PrayerTestimonySection>
  <PrayerHeader>
    <PrayerTitle>Prayer Requests &amp; Testimonies</PrayerTitle>
    <PrayerTabs>
      Prayer Requests <span>|</span> Testimonies
    </PrayerTabs>
  </PrayerHeader>

  <PrayerButtonsContainer>
    <NeedPrayerButton onClick={() => alert("I Need Prayer")}>
      🙏 I Need Prayer
    </NeedPrayerButton>

    <TestimonyButton onClick={() => alert("I Have a Testimony")}>
      ✨ I Have a Testimony
    </TestimonyButton>
  </PrayerButtonsContainer>
</PrayerTestimonySection>
<SalvationSection>
  <SalvationBackground>
    <SalvationCard>
      <SalvationSmallText>If you just received salvation...</SalvationSmallText>
      
      <SalvationCongrats>CONGRATULATIONS!!!</SalvationCongrats>
      
      <SalvationBookText>
        Use the code to download a copy of<br />
        <strong>Now That You Are Born Again</strong><br />
        by
      </SalvationBookText>

      <DownloadButton 
        href="/now_that_you_are_born_again.pdf" 
        download="Now_That_You_Are_Born_Again.pdf"
      >
        CLICKING HERE
      </DownloadButton>
    </SalvationCard>
  </SalvationBackground>
</SalvationSection>
<GrowFaithSection>
  <GrowHeader>
    <GrowTitle>Grow Your Faith</GrowTitle>
    <GrowSubtitle>
      Watch our recent services and grow in the knowledge of God's Word.
    </GrowSubtitle>
  </GrowHeader>

  <VideosGrid>
    {/* Video 1 */}
    <VideoCard onClick={() => window.location.href = "/player?video=1"}>
      <ThumbnailContainer>
        <Thumbnail 
          src="https://picsum.photos/id/1015/600/340" 
          alt="Outreach Service" 
        />
        <PlayOverlay>▶</PlayOverlay>
      </ThumbnailContainer>
      <VideoInfo>
        <VideoTitle>Outreach service testing 222</VideoTitle>
        <VideoDate>Feb 3, 2026</VideoDate>
      </VideoInfo>
    </VideoCard>

    {/* Video 2 */}
    <VideoCard onClick={() => window.location.href = "/player?video=2"}>
      <ThumbnailContainer>
        <Thumbnail 
          src="https://picsum.photos/id/201/600/340" 
          alt="Gospel Podcast" 
        />
        <PlayOverlay>▶</PlayOverlay>
      </ThumbnailContainer>
      <VideoInfo>
        <VideoTitle>ONLINE TO ONSITE PROLIFERATION - The Gospel Podcast</VideoTitle>
        <VideoDate>Jan 31, 2026</VideoDate>
      </VideoInfo>
    </VideoCard>

    {/* Video 3 */}
    <VideoCard onClick={() => window.location.href = "/player?video=3"}>
      <ThumbnailContainer>
        <Thumbnail 
          src="https://picsum.photos/id/870/600/340" 
          alt="Midweek Service" 
        />
        <PlayOverlay>▶</PlayOverlay>
      </ThumbnailContainer>
      <VideoInfo>
        <VideoTitle>Midweek Service</VideoTitle>
        <VideoDate>Jan 31, 2026</VideoDate>
      </VideoInfo>
    </VideoCard>
  </VideosGrid>
</GrowFaithSection>
      </Main>
      <Footer />
    </Container>
  );
};

export default Dashboard;

//mp4